import { useEffect, useState, type HTMLAttributes } from 'react';
import type {
  ConversationMessage,
  ConversationMessageContent,
} from '../../types/conversation-message';
import {
  getUserDisplayTextContent,
  getTextContent,
} from '../../types/conversation-message';
import type { QuoteContent } from '@/utils/chat-quote';
import { isQuoteContent } from '@/utils/chat-quote';
import { CheckmarkSFSymbolMedium } from '@/assets/sf-symbols/medium/checkmark';
import { DocumentOnDocumentSFSymbolMedium } from '@/assets/sf-symbols/medium/document.on.document';
import { hasConversationAttachmentSource } from './conversation-attachment';

type CollectedContent = {
  files: CollectedAttachmentContent[];
  images: CollectedAttachmentContent[];
  audios: CollectedAttachmentContent[];
  videos: CollectedAttachmentContent[];
  memories: CollectedAttachmentContent[];
  quotes: QuoteContent[];
  texts: string[];
};

export type CollectedAttachmentContent = {
  content: ConversationMessageContent;
  contentIndex: number;
};

function hasFileReference(content: ConversationMessageContent): boolean {
  return hasConversationAttachmentSource(content);
}

export function collectUserMessageContent(
  message: ConversationMessage
): CollectedContent {
  const result: CollectedContent = {
    files: [],
    images: [],
    audios: [],
    videos: [],
    memories: [],
    quotes: [],
    texts: [],
  };
  if (!message.content) return result;
  let didCollectUserTexts = false;

  for (
    let contentIndex = 0;
    contentIndex < message.content.length;
    contentIndex += 1
  ) {
    const c = message.content[contentIndex];
    if (isQuoteContent(c)) {
      result.quotes.push(c);
    } else if (c.type === 'text' && c.text) {
      if (message.role === 'user') {
        if (didCollectUserTexts) {
          continue;
        }
        result.texts.push(...getUserDisplayTextContent(message));
        didCollectUserTexts = true;
      } else {
        result.texts.push(c.text);
      }
    } else if (c.type === 'image' && hasFileReference(c)) {
      result.images.push({ content: c, contentIndex });
    } else if (c.type === 'audio' && hasFileReference(c)) {
      result.audios.push({ content: c, contentIndex });
    } else if (c.type === 'video' && hasFileReference(c)) {
      result.videos.push({ content: c, contentIndex });
    } else if (c.type === 'file' && hasFileReference(c)) {
      result.files.push({ content: c, contentIndex });
    } else if (c.type === 'memory') {
      // Structured Memory/App Shot attachment — rendered as a card, never as a
      // raw `[Screen memory: …]` text bubble.
      result.memories.push({ content: c, contentIndex });
    }
  }

  return result;
}

export function collectAssistantMessagesContent(
  messages: ConversationMessage[]
): string[] {
  return messages.flatMap(m => getTextContent(m));
}

export const copyUserMessage = async (message: ConversationMessage) => {
  const { texts, memories } = collectUserMessageContent(message);
  // Copy attached memories as a clean `Memory: <title>` line — never the raw
  // `[Screen memory: …]` wrapper or the trailing hashtag paragraph.
  const memoryLines = memories
    .map(m => (m.content.title ?? '').trim())
    .filter(Boolean)
    .map(title => `Memory: ${title}`);
  await navigator.clipboard?.writeText([...memoryLines, ...texts].join('\n'));
  window.jsb?.MessagesBridge?.trackCopyFromChat('user_message');
  return true;
};

export const copyAssistantMessages = async (
  messages: ConversationMessage[]
) => {
  const texts = collectAssistantMessagesContent(messages);
  await navigator.clipboard?.writeText(texts.join('\n'));
  window.jsb?.MessagesBridge?.trackCopyFromChat('assistant_message');
  return true;
};

export const CopyButton = ({
  onCopy,
  onClick,
  ...props
}: HTMLAttributes<HTMLButtonElement> & {
  onCopy: () => Promise<boolean>;
}) => {
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 1000);
    }
  }, [isCopied]);

  return (
    <button
      {...props}
      onClick={async e => {
        onClick?.(e);
        const success = await onCopy?.();
        if (success) {
          setIsCopied(true);
        }
      }}
    >
      {isCopied ? (
        <CheckmarkSFSymbolMedium className="text-xs" />
      ) : (
        <DocumentOnDocumentSFSymbolMedium />
      )}
    </button>
  );
};
